import React, { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { AccountView, Edition, User, LoginCredentials, RegisterCredentials, FirstLoginCredentials, AuthContextType } from '@/types/auth'
import { authService } from '@/services/authService'
import { useLocale } from '@/contexts/LocaleContext'
import { setSentryUser } from '@/config/sentry'

const ACTIVE_VIEW_STORAGE_KEY = 'active_view'

/** Normalize whatever the backend/localStorage holds into a valid view. */
const toAccountView = (value: unknown): AccountView =>
  value === 'respondent' ? 'respondent' : 'researcher'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { syncFromUser } = useLocale()
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // Airbnb-style view toggle. Seeded from localStorage so a page reload doesn't
  // flash the wrong view; authoritative value comes from the user's
  // `active_view` (last used view, restored on login). Default: researcher.
  const [currentView, setCurrentView] = useState<AccountView>(() =>
    toAccountView(localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY)),
  )

  const applyUserView = (userData: User) => {
    const view = toAccountView(userData.active_view)
    setCurrentView(view)
    localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, view)
  }

  console.log('AuthProvider rendering, isLoading:', isLoading, 'isAuthenticated:', !!user && !!token)

  const isAuthenticated = !!user && !!token

  // Attribute every Sentry event to the current user (cleared on logout), so an
  // issue tells us *which* researcher/respondent hit it. Covers all auth paths.
  useEffect(() => {
    setSentryUser(user ? { id: user.id, email: user.email, is_researcher: user.is_researcher } : null)
  }, [user])

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      // Dev-only: skip the login panel entirely (no backend needed).
      // Enable by setting VITE_AUTH_BYPASS=true in frontend/.env.local
      if (import.meta.env.VITE_AUTH_BYPASS === 'true') {
        const mockUser: User = {
          id: 1,
          email: 'dev@facewise.local',
          first_name: 'Dev',
          last_name: 'User',
          is_researcher: true,
          preferred_language: 'en',
          active_view: 'researcher',
          plan: 'full',
          has_active_access: true,
          access_expires_at: null,
          access_days_left: null,
          can_view_admin_panel: true,
          // Set VITE_EDITION=basic in .env.local to browse the bypassed dev
          // session as a FaceWise (single-measurement) account.
          edition: import.meta.env.VITE_EDITION === 'basic' ? 'basic' : 'pro',
        }
        console.warn('[AuthContext] VITE_AUTH_BYPASS active — using mock user, login skipped')
        setUser(mockUser)
        setToken('dev-bypass-token')
        syncFromUser(mockUser.preferred_language)
        setIsLoading(false)
        return
      }

      console.log('Initializing auth...')
      const accessToken = authService.getAccessToken()
      const refreshToken = authService.getRefreshToken()
      
      console.log('Found tokens:', { accessToken: !!accessToken, refreshToken: !!refreshToken })

      if (accessToken && refreshToken) {
        // Check if access token is expired
        if (authService.isTokenExpired(accessToken)) {
          console.log('Access token expired, trying to refresh...')
          try {
            // Try to refresh the token
            const newTokens = await authService.refreshTokenWithToken(refreshToken)
            authService.setTokens(newTokens.access, newTokens.refresh)
            setToken(newTokens.access)
            
            // Get user data
            const userData = await authService.getCurrentUser()
            setUser(userData)
            applyUserView(userData)
            syncFromUser(userData.preferred_language)
            console.log('Token refreshed and user data loaded:', userData)
          } catch (error) {
            console.log('Token refresh failed:', error)
            // Refresh failed, clear tokens
            authService.clearTokens()
            setToken(null)
            setUser(null)
          }
        } else {
          console.log('Access token still valid, getting user data...')
          // Token is still valid
          setToken(accessToken)
          try {
            const userData = await authService.getCurrentUser()
            setUser(userData)
            applyUserView(userData)
            syncFromUser(userData.preferred_language)
            console.log('User data loaded:', userData)
          } catch (error) {
            console.log('Failed to get user data:', error)
            // Token might be invalid, clear it
            authService.clearTokens()
            setToken(null)
            setUser(null)
          }
        }
      } else {
        console.log('No tokens found, user not authenticated')
      }
      console.log('Auth initialization complete, setting loading to false')
      setIsLoading(false)
    }

    initializeAuth()
  }, [])

  // Global handler: any authenticated request that gets a 401 (see baseService)
  // means the session is dead — drop it so ProtectedRoute sends the user to the
  // login screen instead of flashing a broken dashboard.
  useEffect(() => {
    const onUnauthorized = () => {
      authService.clearTokens()
      setToken(null)
      setUser(null)
    }
    window.addEventListener('auth:unauthorized', onUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized)
  }, [])

  const login = async (credentials: LoginCredentials): Promise<void> => {
    console.log('Login called with credentials:', credentials)
    try {
      setIsLoading(true)
      console.log('Calling authService.login...')
      const response = await authService.login(credentials)
      console.log('Login response received:', response)
      
      // Store tokens
      authService.setTokens(response.access, response.refresh)
      setToken(response.access)
      setUser(response.user)
      applyUserView(response.user)
      syncFromUser(response.user.preferred_language)
      console.log('Login successful, user and token set')
    } catch (error) {
      console.error('Login failed with error:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      })
      throw error
    } finally {
      console.log('Setting isLoading to false')
      setIsLoading(false)
    }
  }

  // First-login link flow: set the password, then establish the session in
  // context (token + user + view) exactly like login/register — otherwise the
  // app stays "unauthenticated" and ProtectedRoute bounces the user to /login.
  // Returns the user so the page can route to the panel for their view.
  const firstLogin = async (credentials: FirstLoginCredentials): Promise<User> => {
    try {
      setIsLoading(true)
      const response = await authService.firstLogin(credentials)
      setToken(response.access)
      setUser(response.user)
      applyUserView(response.user)
      syncFromUser(response.user.preferred_language)
      return response.user
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    try {
      setIsLoading(true)
      const response = await authService.register(credentials)
      
      // Store tokens
      authService.setTokens(response.access, response.refresh)
      setToken(response.access)
      setUser(response.user)
      applyUserView(response.user)
      syncFromUser(response.user.preferred_language)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authService.logout()
    } catch (error) {
      console.warn('Logout error:', error)
    } finally {
      authService.clearTokens()
      setToken(null)
      setUser(null)
    }
  }

  // Airbnb-style "switch to researcher/respondent": flip the view immediately,
  // then persist it best-effort so the next login restores it. A failed PATCH
  // must not block the switch — the view is still applied locally.
  const switchView = (view: AccountView): void => {
    setCurrentView(view)
    localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, view)
    setUser((prev) => (prev ? { ...prev, active_view: view } : prev))
    if (import.meta.env.VITE_AUTH_BYPASS === 'true') return
    authService.updateActiveView(view).catch((error) => {
      console.warn('Failed to persist active view:', error)
    })
  }

  /**
   * Move this account between the editions. Admins only — the button that calls
   * it is behind `can_view_admin_panel` and so is the endpoint.
   *
   * Unlike `switchView` this is not optimistic: the answer is the whole user
   * payload and we take the edition from it, because the backend is the one
   * that decides whether the switch was allowed. A failure leaves the session
   * exactly as it was and throws, so the caller can say so.
   */
  const switchEdition = async (edition: Edition): Promise<void> => {
    // The bypassed dev session has no backend to ask; flip it locally so the
    // edition can still be compared screen by screen without logging in.
    if (import.meta.env.VITE_AUTH_BYPASS === 'true') {
      setUser((prev) => (prev ? { ...prev, edition } : prev))
      return
    }
    const updated = await authService.switchEdition(edition)
    setUser(updated)
  }

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    firstLogin,
    logout,
    isLoading,
    isAuthenticated,
    currentView,
    switchView,
    switchEdition,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
